import { COMPONENT_SOURCE_TEMPLATES, EXPOSED_SYMBOLS } from '@aeppic/dynamic-components-v3'
import { controlNameForPlaceholder, controlPreloadIdentity, normalizeControlLookupVariant, resolveControlSelector, transformControlSelector, type ControlLookupVariant } from '../forms/preload-controls.js'
import * as Types from '@aeppic/types'

import uuid from  '@aeppic/shared/uuid'
// import { buildDeferred, Deferred } from  '@aeppic/shared/defer'

import { lookupDefaultControlName } from '../utils/control-names'
import DynamicComponent from '../dynamic/dynamic-component'
import { perf } from '../perf.js'
import createEventForwarders from '../utils/create-event-forwarders'
import { Form, FormFieldAtPlaceholder } from '../../model/form.js'
import Aeppic from 'aeppic/index.js'
import { ControlLookup, ControlMatchStrategy } from '../lookup/control-lookup.js'
import { LookupTrace } from '../lookup/form-oriented-lookup.js'
import { isDocument } from '../../model/is.js'
import { ParsedField } from '@aeppic/forms-parser'
import { useServerLookup } from '../lookup/client-lookup.js'

const DEFAULT_NAMESPACE = 'default'

export function parseControlName(controlName: string): { namespace: string, name: string } {
  let name = controlName
  let namespace = ''

  if (controlName.includes(':')) {
    const parts = controlName.split(':', 2)

    if (parts.length === 1) {
      [name] = parts
    } else {
      [namespace, name] = parts
    }
  }

  return {
    namespace: namespace.trim().toLowerCase(),
    name: name.trim().toLowerCase(),
  }
}

export default {
  render() {
    const _vm = this
    const _h = _vm.$createElement
    const _c = _vm._self._c || _h

    if (_vm.canRender) {
      return _vm._c(_vm.dynamicComponent.id, {
        key: _vm.dynamicComponent.document.id,
        tag: _vm.component,
        class: { 'ae-sensitive': (_vm.parsedFieldInfo?.tags?.sensitive != null && _vm.parsedFieldInfo.tags.sensitive !== false && _vm.parsedFieldInfo.tags.sensitive !== 'false') || _vm.paramsToBindTo?.supportSensitive === true || _vm.paramsToBindTo?.supportSensitive === 'true' },
        props: {
          Aeppic: _vm.Aeppic,
          control: _vm.dynamicComponent.document,
          params: _vm.paramsToBindTo,
          document: _vm.documentToBindTo,
          form: _vm.formToBindTo,
          fieldInfo: _vm.parsedFieldInfo,
          fieldName: _vm.fieldNameToBindTo,
          label: _vm.label ?? _vm.placeholder?.label ?? '',
          protected: _vm.protectedField,
          readonly: _vm.readonly,
          uniqueId: _vm.uniqueId,
          validate: _vm.validate,
        },
        on: createEventForwarders(_vm._events, _vm)
      })  
    }

    if (_vm.isDeveloper && (_vm.error || (_vm.dynamicComponent?.id && !_vm.canRender))) {
      return _vm.renderLookupError(_h)
    }

    return _vm._e()
  },
  renderError(h, err) {
    return h('pre', { staticClass: 'ae-error', style: { color: 'red' }}, 'ae-control:' + this.name + ' ' +  err.stack)
  },
  inject: ['getAeppicContext'],
  props: {
    variant: { type: Object, default: () => ({}) },
    name: String,
    params: Object,
    field: Object, // ParsedField
    fieldName: String,
    document: Object,
    form: Object,
    readonly: Boolean,
    validate: {
      type: Boolean,
      default: true
    },
    fieldValidationErrors: {
      type: Object,
      default: null
    },
    label: String,
    watch: {
      type: Boolean,
      default: true
    },
    watchControl: {
      type: Boolean,
      default: null
    },
    watchControlRevisions: {
      type: Boolean,
      default: null
    },
    disableDefaultControl: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: Object,
      default: null
    },
  },
  data() {
    const Aeppic = this.getAeppicContext('ae-control-selector:-', this.document)

    const isDeveloper = Aeppic.Account && Aeppic.Account.data.allowDevMode

    const watch = typeof this.watchControl === 'boolean' ? this.watchControl : isDeveloper
    const watchRevisions =  Aeppic.Developer.isServerInRemoteDeveloperMode ? false
                          : typeof this.watchControlRevisions === 'boolean' ? this.watchControlRevisions
                          : isDeveloper

    const dynamicComponent = new DynamicComponent('control', Aeppic, {
      formId: 'control-form',
      template: COMPONENT_SOURCE_TEMPLATES.control,
      exposedSymbols: EXPOSED_SYMBOLS.control,
      watch,
      watchRevisions
    })

    return {
      Aeppic,
      uniqueId: uuid(),
      dynamicComponent,
      documentToBindTo: null,
      error: null,
      isDeveloper,
      lookupDebug: null,
      paramsSignature: null,
      formToBindTo: null,
      fieldInfo: null,
      parsedFieldInfo: null,
      fieldNameToBindTo: null,
      protectedField: false,
      paramsToBindTo: {}
    }
  }, 
  beforeDestroy() {
    this.dynamicComponent.destroy()
    this.Aeppic.release()
  },
  computed: {
    canRender() {
      return this.dynamicComponent != null && this.dynamicComponent.id && this.documentToBindTo && this.formToBindTo && this.checkFieldValidity()
    }
  },
  methods: {
    checkFieldValidity() {
      if (!this.formToBindTo) {
        return false
      }

      if (this.fieldNameToBindTo && !this.formToBindTo.info.fields.hasOwnProperty(this.fieldNameToBindTo)) {
        return false
      }

      return true
    },
    renderLookupError(h) {
      const debug = this.lookupDebug ?? this.buildNotReadyDebug()

      return h('div', {
        staticClass: 'ae-dev-error ae-dev-error--control-lookup',
        attrs: {
          'data-ae-control-lookup-error': 'true',
          'data-ae-control-name': this.name || '',
          'data-ae-field-name': this.fieldNameToBindTo || ''
        },
        style: {
          flex: '1 1 100%',
          width: '100%',
          minWidth: '0',
          boxSizing: 'border-box',
          border: '1px solid #d92d20',
          borderRadius: '6px',
          background: '#fff3f0',
          color: '#7a271a',
          padding: '0.75rem',
          fontSize: '0.8125rem',
          lineHeight: '1.35'
        }
      }, [
        h('strong', this.error ? 'ae-control lookup failed' : 'ae-control did not render'),
        h('div', { style: { marginTop: '0.25rem' } }, this.error || 'Control was found, but canRender is false.'),
        h('pre', {
          style: {
            margin: '0.5rem 0 0',
            padding: '0.5rem',
            overflow: 'auto',
            maxHeight: '18rem',
            background: 'rgba(255,255,255,0.72)',
            border: '1px solid rgba(122,39,26,0.2)',
            borderRadius: '4px',
            color: '#55160c',
            whiteSpace: 'pre-wrap'
          }
        }, JSON.stringify(debug, null, 2))
      ])
    },
    buildNotReadyDebug() {
      return {
        requestedControl: this.name || '',
        fieldName: this.fieldNameToBindTo || '',
        hasDocument: !!this.documentToBindTo,
        document: this.documentToBindTo ? {
          id: this.documentToBindTo.id,
          v: this.documentToBindTo.v,
          form: this.documentToBindTo.f
        } : null,
        hasForm: !!this.formToBindTo,
        form: this.formToBindTo ? {
          id: this.formToBindTo.id,
          v: this.formToBindTo.v,
          name: this.formToBindTo.data?.name,
          hasField: this.fieldNameToBindTo ? this.formToBindTo.info.fields.hasOwnProperty(this.fieldNameToBindTo) : null
        } : null,
        placeholder: this.placeholder ? {
          index: this.placeholder.index,
          offset: this.placeholder.offset,
          label: this.placeholder.label,
          control: this.placeholder.control ?? null
        } : null,
        fieldInfo: this.parsedFieldInfo ? {
          name: this.parsedFieldInfo.name,
          type: this.parsedFieldInfo.type,
          subType: this.parsedFieldInfo.subType,
          cardinality: this.parsedFieldInfo.cardinality ?? null
        } : null,
        dynamicComponent: {
          id: this.dynamicComponent?.id ?? null,
          document: this.dynamicComponent?.document ? {
            id: this.dynamicComponent.document.id,
            v: this.dynamicComponent.document.v,
            name: this.dynamicComponent.document.data?.name
          } : null
        },
        canRenderChecks: {
          hasDynamicComponent: !!this.dynamicComponent,
          hasDynamicComponentId: !!this.dynamicComponent?.id,
          hasDocumentToBindTo: !!this.documentToBindTo,
          hasFormToBindTo: !!this.formToBindTo,
          fieldIsValid: this.formToBindTo ? this.checkFieldValidity() : null
        }
      }
    },
  },
  mounted() {
    this.Aeppic.setContextRootElement(this.$el)
  },
  updated() {
    this.Aeppic.setContextRootElement(this.$el)
  },
  created() {
    this.loadControl = async function() {
      const detail = this.name || this.fieldName || ''
      await perf.track('control:load', async () => {
      this.paramsSignature = JSON.stringify(this.params || {})
      this.lookupDebug = null
      this.error = null

      // FIXME: improve load control behavior. If a control is already loaded it will get updated with document, form, params before we decided if we need to refresh the control
      this.documentToBindTo = await this.document
      this.formToBindTo = await this.loadForm(this.documentToBindTo)
      
      if (!this.documentToBindTo) {
        return
      }
      
      if (!this.formToBindTo) {
        return
      }

      const placeholderParams = this.placeholder ? this.formToBindTo.getPlaceholderControlParams(this.placeholder.index) : {}
      this.paramsToBindTo = { ...placeholderParams, ...this.params }

      const fieldName = this.field?.name ?? this.fieldName
      this.fieldNameToBindTo = fieldName

      if (fieldName && !this.checkFieldValidity()) {
        this.error = `Unknown field '${fieldName}'. Does not exist in form '${this.formToBindTo.id}@${this.formToBindTo.v}'`
        this.dynamicComponent.reset()
        return
      }

      if (fieldName) {
        this.parsedFieldInfo = this.formToBindTo.getField(fieldName)
        this.fieldInfo = this.parsedFieldInfo
        this.protectedField = this.documentToBindTo.$protectedFields?.includes(fieldName)
      } else {
        this.parsedFieldInfo = null
        this.fieldInfo = null
        this.protectedField = false
      }

      this.error = null

      // We do not pass on the placeholder offset for temporary forms
      // as the placeholder offset would be used by the findControl function
      // to look up the control from the server which does not know this form version
      const placeholderOffset = this.formToBindTo.isTemporary ? null : this.placeholder?.offset
      const formInfo = `${this.formToBindTo.isTemporary ? '[TEMP] ' : ''}${this.formToBindTo.data.name} (${this.formToBindTo.id}@${this.formToBindTo.v})`
      const contextPrefix = `ae-control-selector:{${this.name || ''} in ${formInfo}}:`
      const lookupTrace = new LookupTrace()
      const lookupOptions = this.Aeppic.Features.getOptionsFor('control-lookup') ?? {}
      let controlDocument = null
      try {
        controlDocument = await perf.track('control:lookup', () =>
          findControlForRendering(this.Aeppic, this.formToBindTo, this.parsedFieldInfo, this.name, placeholderOffset,
            this.disableDefaultControl, this.isDeveloper ? lookupTrace : undefined, this.variant),
          this.name || fieldName || '')
      } catch (error) {
        this.Aeppic.renameContext?.(`${contextPrefix}lookup-error[${fieldName}@${placeholderOffset}]`)
        this.error = `Error while looking up control {${this.name || ''}} for field [${fieldName || ''}]: ${error?.message ?? error}`
        this.lookupDebug = buildControlLookupDebug({
          controlLookupEnabled: this.Aeppic.Features.isEnabled('control-lookup'),
          controlName: this.name,
          disableDefaultControl: this.disableDefaultControl,
          error,
          fallback: lookupOptions.fallback,
          field: this.parsedFieldInfo,
          fieldName,
          form: this.formToBindTo,
          placeholderOffset,
          strategy: lookupOptions.strategy ?? 'full',
          trace: lookupTrace
        })
        this.dynamicComponent.reset()
        return null
      }

      if (controlDocument) {
        this.Aeppic.renameContext?.(`${contextPrefix}${controlDocument.id}:${controlDocument.data.name ?? '?'}[${fieldName}@${placeholderOffset}]`)
        this.lookupDebug = null
        try {
          await this.dynamicComponent.refresh(controlDocument)
          this.$forceUpdate()
        } catch (error) {
          this.error = `Error while preparing control {${controlDocument.data?.name || controlDocument.id}} for field [${fieldName || ''}]: ${error?.message ?? error}`
          this.lookupDebug = {
            error: {
              message: error?.message ?? String(error),
              stack: error?.stack
            },
            matchedControl: {
              id: controlDocument.id,
              v: controlDocument.v,
              name: controlDocument.data?.name,
              namespace: controlDocument.data?.namespace,
              packages: controlDocument.data?.packages
            }
          }
          this.dynamicComponent.reset()
          this.$forceUpdate()
        }
      }
      else if (fieldName) {
        this.Aeppic.renameContext?.(`${contextPrefix}-[${fieldName}@${placeholderOffset}]`)
        this.error = `Could not find matching control {${this.name || ''}} for field [${fieldName}]`
        this.lookupDebug = buildControlLookupDebug({
          controlLookupEnabled: this.Aeppic.Features.isEnabled('control-lookup'),
          controlName: this.name,
          disableDefaultControl: this.disableDefaultControl,
          fallback: lookupOptions.fallback,
          field: this.parsedFieldInfo,
          fieldName,
          form: this.formToBindTo,
          placeholderOffset,
          strategy: lookupOptions.strategy ?? 'full',
          trace: lookupTrace
        })
        this.dynamicComponent.reset()
        return null
      }
      else {
        this.Aeppic.renameContext?.(contextPrefix)
        this.error = `Could not find matching standalone control {${this.name || ''}}`
        this.lookupDebug = buildControlLookupDebug({
          controlLookupEnabled: this.Aeppic.Features.isEnabled('control-lookup'),
          controlName: this.name,
          disableDefaultControl: this.disableDefaultControl,
          fallback: lookupOptions.fallback,
          field: null,
          fieldName: null,
          form: this.formToBindTo,
          placeholderOffset,
          strategy: lookupOptions.strategy ?? 'full',
          trace: lookupTrace
        })
        this.dynamicComponent.reset()
        return null
      }
      }, detail)
    }

    this.loadForm = async function(d: Types.Document) {
      return this.form || this.Aeppic.getFormForDocument(d)
    }

    // A document notification may rerender the component, but its lookup stays pinned until reload.
    this.dynamicComponent.on('invalidated', () => this.loadControl())

    this.loadControl()
  },
  watch: {
    variant: { deep: true, handler() { this.loadControl() } },
    name() {
      this.loadControl()
    },
    document() {
      this.loadControl()
    },
    fieldName() {
      this.loadControl()
    },
    // document(newDocument, previous) {
    //   if (newDocument == null || previous == null) {
    //     this.configureControl()
    //   } 
    // },
    form() {
      this.loadControl()
    },
    params() {
      const paramsSignature = JSON.stringify(this.params || {})

      if (paramsSignature === this.paramsSignature) {
        return
      }

      this.loadControl()
    },
  }
}

const STRATEGY_NAME_TO_ENUM = {
  'default-only': ControlMatchStrategy.DEFAULT_ONLY,
  'full': ControlMatchStrategy.FULL,
  'system': ControlMatchStrategy.SYSTEM,
  'classic': null
}

const individualCaches = new WeakMap<object, Map<string, Promise<Types.Document | null>>>()
function instanceCache(registry, aeppic) {
  const owner = aeppic.Forms ?? aeppic
  let cache = registry.get(owner)
  if (!cache) { cache = new Map(); registry.set(owner, cache) }
  return cache
}

function cachedLookup(cache, key, resolve) {
  if (!cache.has(key)) {
    const pending = Promise.resolve().then(resolve).catch(error => { cache.delete(key); throw error })
    cache.set(key, pending)
  }
  return cache.get(key)
}

/** Developer permission enables diagnostics on a miss, not uncached lookup on every render. */
export async function findControlForRendering(aeppic: Aeppic, form: Types.Reference, field: ParsedField, controlName: string, placeholderOffset: number, disableDefaultControl: boolean, diagnosticTrace?: LookupTrace, variant: ControlLookupVariant = {}): Promise<Types.Document> {
  const control = await findControl(aeppic, form, field, controlName, placeholderOffset, disableDefaultControl, undefined, variant)
  if (control || !diagnosticTrace) return control
  return findControl(aeppic, form, field, controlName, placeholderOffset, disableDefaultControl, diagnosticTrace, variant)
}

export async function findControl(aeppic: Aeppic, form: Types.Reference, field: ParsedField, controlName: string, placeholderOffset: number, disableDefaultControl: boolean, lookupTrace?: LookupTrace, variant: ControlLookupVariant = {}): Promise<Types.Document> {
  const normalized = normalizeControlLookupVariant(variant)
  const identity = controlPreloadIdentity(aeppic, form, normalized)
  const selector = transformControlSelector(controlName, field, normalized)
  const modified = Object.keys(normalized).length > 0
  const options = aeppic.Features.getOptionsFor('control-lookup') ?? {}
  const strategy = STRATEGY_NAME_TO_ENUM[options.strategy ?? 'full']
  const classic = () => classicFindControl(aeppic, form, field?.name, controlName, disableDefaultControl || modified, lookupTrace, normalized)
  const resolve = async () => {
    const canUseMap = !(form as Form).isTemporary && !disableDefaultControl
      && (placeholderOffset != null || (useServerLookup(aeppic, 'controls') && !lookupTrace))
    const definition = !canUseMap ? null : typeof (form as any).getPlaceholders === 'function' ? form : await aeppic.getForm(form)
    const matches = placeholder => transformControlSelector(controlNameForPlaceholder(placeholder), placeholder.field ?? null, normalized) === selector
      && JSON.stringify([placeholder.field?.name, placeholder.field?.type, placeholder.field?.subType, placeholder.field?.cardinality])
        === JSON.stringify([field?.name, field?.type, field?.subType, field?.cardinality])
    // Table cells pass fields without offsets. Reuse a map only for an equivalent real placeholder.
    const placeholder = (definition as any)?.getPlaceholders().find(p => placeholderOffset == null ? matches(p) : p.offset === placeholderOffset)
    const matchesPlaceholder = !!placeholder && matches(placeholder)
    const resolvedOffset = placeholder?.offset
    if (!lookupTrace && matchesPlaceholder) {
      let preloaded = aeppic.Forms?.preloadedControl(form, resolvedOffset, normalized)
      if (preloaded !== undefined) return preloaded
    }
    if (!aeppic.Features.isEnabled('control-lookup') || strategy == null) return classic()
    let control: Types.Document | null
    if (matchesPlaceholder && useServerLookup(aeppic, 'controls') && !lookupTrace) {
      const map = await aeppic.Forms.preloadControlsForForm(definition as Form, normalized)
      return map.get(resolvedOffset) ?? null
    } else {
      control = await new ControlLookup(aeppic, strategy).findControlDocument(form.id, field, controlName, lookupTrace, normalized)
    }
    return control ?? (options.fallback === true ? await classic() : null)
  }
  if (lookupTrace || options.cache !== true) return resolve()
  const key = JSON.stringify([identity, placeholderOffset, field, selector, disableDefaultControl])
  return cachedLookup(instanceCache(individualCaches, aeppic), key, resolve)
}

export async function classicFindControl(aeppic: Aeppic, formReference: Types.IdOrReference, fieldName: string, controlName: string, disableDefaultControl, lookupTrace?: LookupTrace, variant?: ControlLookupVariant): Promise<Types.Document> {
  const form = await aeppic.getForm(formReference)
  if (!form) throw new Error(`Missing form ${JSON.stringify(formReference)}`)
  const resolvedSelector = variant === undefined ? undefined : resolveControlSelector(controlName, form.info.fields[fieldName] ?? null, variant)
  const info = await _findControl(aeppic, { id: form.id, v: form.v }, fieldName, controlName, disableDefaultControl, lookupTrace, resolvedSelector)
  return info?.controlDocument ?? null
}

/** 
 * 
 * Lookup rule:
 *
 * Check each of the following folder locations for a control matching
 * that name and namespace. If nothing found also check for namespace 'default' before
 * moving on to next location.
 *
 * Get form-folder -> find control locations defined from there (TODO)
 * Get application the form is located in -> find control locations defined from there (TODO)
 * Look in /System/Controls (controls-folder) folder 
 * Look in /System/Controls/Fallback Controls 
 *
 * Look for any control with matching name (namespace:name) anywhere 
 *
 */

async function _findControl(aeppic, formId, fieldName, controlName, disableDefaultControl, lookupTrace?: LookupTrace, resolvedSelector?: { namespace: string, name: string }) {
  const form = await aeppic.getForm(formId)
  const controlDocument = await findControlByName(aeppic, form, controlName, lookupTrace, resolvedSelector)

  if (controlDocument) {
    return {
      controlDocument,
      controlName
    }
  } else if (fieldName) {
    const field = form.info.fields[fieldName]

    if (!field) {
      return {
        controlDocument,
        controlName
      }
    }

    if (disableDefaultControl) {
      lookupTrace?.addStep({ operation: 'classic:default-control-disabled', documentId: formId, additionalInfo: { fieldName, controlName } })
      return { 
        controlDocument: null, 
        controlName
      }
    }

    const defaultTypeControlName = lookupDefaultControlName(field)
    lookupTrace?.addStep({ operation: 'classic:default-control-name', documentId: formId, additionalInfo: { fieldName, defaultTypeControlName } })
    const typedBasedControl = await findControlByName(aeppic, form, defaultTypeControlName, lookupTrace)

    if (typedBasedControl) {
      return {
        controlDocument: typedBasedControl,
        controlName: defaultTypeControlName
      }
    } else {
      return {
        controlDocument: aeppic.get('default-text-control'),
        controlName: 'default-text-control'
      }
    }
  } 
}

async function findControlByName(aeppic, form, controlName, lookupTrace?: LookupTrace, resolvedSelector?: { namespace: string, name: string }) {
  if (!controlName && !resolvedSelector) {
    lookupTrace?.addStep({ operation: 'classic:empty-control-name', documentId: form.id })
    return null
  }

  const { namespace, name } = resolvedSelector
    ? { namespace: resolvedSelector.namespace.toLowerCase(), name: resolvedSelector.name.toLowerCase() }
    : parseControlName(controlName)

  // const locations = [
  //   form.id,
  //   // form.p, // NOT YET EXPOSED .. but should look into form-folder/controls multi-refs
  //   // also the form.p cannot be used anyway. it should be the current p of the most recent form
  //   // which might have moved since the original date
  //   '2187d93e-90b9-46cd-9956-c665a08f0aff',
  //   'controls-folder',
  //   '91f205bc-9989-4fbe-8966-bd4ba9777c26'
  // ] 

  const locations = await getControlLocations(aeppic, form, lookupTrace)

  for (const location of locations) {
    const matchingControl = await lookupControlInLocation(aeppic, location, namespace, name, lookupTrace)
    if (matchingControl) {
      return matchingControl
    }
  }
}

async function getControlLocations(aeppic, form, lookupTrace?: LookupTrace) {
  // const ancestors = await aeppic.getAll(form.document.a)
  const locations = []

  // for (let ancestor of ancestors) {
  //   if ((ancestor.f.id === 'application-form') || (ancestor.p === 'apps')) {
  //     let controlFolders = await aeppic.find(`p:"${ancestor.id}" AND data.name:controls`)

  //     for (let controlFolder of controlFolders) {
  //       locations.unshift(controlFolder.id)
  //     }
  //   }
  // }
  
  locations.push('controls-folder')
  locations.push('91f205bc-9989-4fbe-8966-bd4ba9777c26') // default controls

  lookupTrace?.addStep({ operation: 'classic:locations', documentId: form.id, additionalInfo: { locations } })
  return locations
}

async function lookupControlInLocation(aeppic, locationDocumentId, namespace, name, lookupTrace?: LookupTrace) {
  const controls = await aeppic.find(`p:${locationDocumentId} f.id:control-form`, { size: 1000 })
  lookupTrace?.addStep({ operation: 'classic:find-in-location', documentId: locationDocumentId, additionalInfo: { namespace, name, controls: controls.map(control => ({ id: control.id, namespace: control.data?.namespace, name: control.data?.name, types: control.data?.types, limitTypes: control.data?.limitTypes, cardinality: control.data?.cardinality })) } })

  for (const control of controls) {
    if (isMatchingControl(control, namespace, name)) {
      return control
    }
  }

  for (const control of controls) {
    if (isMatchingControl(control, DEFAULT_NAMESPACE, name)) {
      return control
    }
  }

  return null
}

function buildControlLookupDebug({ controlLookupEnabled, controlName, disableDefaultControl, error = null, fallback, field, fieldName, form, placeholderOffset, strategy, trace }) {
  return {
    requestedControl: controlName || '',
    error: error ? {
      message: error.message ?? String(error),
      stack: error.stack
    } : null,
    controlLookupEnabled,
    strategy,
    fallback: fallback === true,
    disableDefaultControl: disableDefaultControl === true,
    form: {
      id: form?.id,
      v: form?.v,
      name: form?.data?.name,
      isTemporary: form?.isTemporary === true
    },
    field: field ? {
      name: fieldName,
      type: field.type,
      subType: field.subType,
      cardinality: field.cardinality ?? null,
      defaultValueType: Object.prototype.toString.call(field.defaultValue).slice(8, -1).toLowerCase()
    } : null,
    placeholderOffset: placeholderOffset ?? null,
    resolvedSelector: {
      namespace: trace?.namespace ?? '',
      name: trace?.name ?? ''
    },
    looked: trace?.steps?.map(summarizeLookupStep) ?? []
  }
}

function summarizeLookupStep(step) {
  return {
    operation: step.operation,
    documentId: step.documentId,
    ...summarizeLookupAdditionalInfo(step.additionalInfo)
  }
}

function summarizeLookupAdditionalInfo(info) {
  if (!info) {
    return {}
  }

  const result = { ...info }

  if (result.query) {
    result.query = String(result.query)
  }

  if (Array.isArray(result.controls)) {
    result.controls = result.controls.map(control => {
      if (!control || typeof control !== 'object') {
        return control
      }

      return {
        id: control.id,
        namespace: control.namespace ?? control.data?.namespace,
        name: control.name ?? control.data?.name,
        types: control.types ?? control.data?.types,
        limitTypes: control.limitTypes ?? control.data?.limitTypes,
        cardinality: control.cardinality ?? control.data?.cardinality,
      }
    })
  }

  if (result.control) {
    result.control = {
      id: result.control.id,
      namespace: result.control.data?.namespace,
      name: result.control.data?.name,
      types: result.control.data?.types,
      limitTypes: result.control.data?.limitTypes,
      cardinality: result.control.data?.cardinality,
    }
  }

  return Object.fromEntries(Object.entries(result).filter(([, value]) => value !== undefined))
}

function isMatchingControl(controlDocument, namespace, name) {
  if (controlDocument.data.packages == null) {
    return false
  }

  const controlName = String(controlDocument.data.name ?? '').trim().toLowerCase()
  const controlNamespace = String(controlDocument.data.namespace ?? '').trim().toLowerCase()

  return controlName === name && controlNamespace === namespace
}
